import debounce from "debounce";
import { trackFilterElementEvent } from "./segment-tracks";

/**
 * @param {Object} config
 * @param {string[]} config.pickerSelectors
 * @param {string} config.searchInputSelector
 * @param {string} config.resultsQuantitySelector
 * @param {string} config.gridItemSelector
 * @param {string[]} config.urlParamMapping
 * @param {string} [config.titleClass]
 * @param {Object.<string, string>} [config.sectionGridMapping]
 */
export function initializeFilterComponent({
  pickerSelectors,
  searchInputSelector,
  resultsQuantitySelector,
  gridItemSelector,
  urlParamMapping,
  titleClass,
  sectionGridMapping,
}) {
  const pickers = /** @type {HTMLSelectElement[]} */ (
    pickerSelectors
      .map((selector) => document.querySelector(selector))
      .filter((picker) => picker instanceof HTMLSelectElement)
  );
  const searchInput = document.querySelector(searchInputSelector);
  const resultsQuantityElem = document.querySelector(resultsQuantitySelector);
  const gridItems = document.querySelectorAll(gridItemSelector);

  if (pickers.length > 0 && searchInput instanceof HTMLInputElement) {
    const makeFilter = () => {
      return pickers
        .map((picker) =>
          picker.value && picker.value !== picker.options[0].value
            ? `.${picker.value}`
            : "",
        )
        .join("");
    };

    const resetFilter = () => {
      pickers.forEach((picker) => {
        picker.selectedIndex = 0;
      });
    };

    const resetSearch = () => {
      searchInput.value = "";
    };

    const updateGridUI = (filter, searchTerm = "") => {
      let filterCount = 0;
      /** @type {Record<string, number>} */
      const sectionCounts = {};

      gridItems.forEach((item) => {
        const titleElement = titleClass
          ? item.querySelector(`.${titleClass}`)
          : item;
        const matchesFilter = !filter || item.matches(filter);
        const matchesSearch =
          !searchTerm ||
          (titleElement &&
            titleElement.textContent
              .toLowerCase()
              .includes(searchTerm.toLowerCase()));

        if (matchesFilter && matchesSearch) {
          item.classList.remove("hidden");
          filterCount++;

          if (sectionGridMapping) {
            for (const [sectionId, gridId] of Object.entries(
              sectionGridMapping,
            )) {
              const grid = document.querySelector(`#${gridId}`);
              if (grid && grid.contains(item)) {
                sectionCounts[sectionId] = (sectionCounts[sectionId] || 0) + 1;
              }
            }
          }
        } else {
          item.classList.add("hidden");
        }
      });

      if (sectionGridMapping) {
        for (const [sectionId] of Object.entries(sectionGridMapping)) {
          const section = document.querySelector(`#${sectionId}`);
          if (section) {
            if ((sectionCounts[sectionId] || 0) > 0) {
              section.classList.remove("hidden");
            } else {
              section.classList.add("hidden");
            }
          }
        }
      }

      updateFilterCount(filterCount);
      return filterCount;
    };

    const updateFilterCount = (filterCount) => {
      if (resultsQuantityElem) {
        if (filterCount === 1) {
          resultsQuantityElem.textContent = "1 result";
        } else if (filterCount > 1) {
          resultsQuantityElem.textContent = `${filterCount} results`;
        } else {
          resultsQuantityElem.textContent =
            "Sorry, no results were found. Please try adjusting your filters or search term.";
        }
      }
    };

    /**
     * @param {any} evt
     */
    const trackFilterEvent = (evt) => {
      const eventType = evt?.type;
      const target = /** @type {any} */ (evt?.target);
      const props = {
        type: target?.type,
        field_name: target?.id,
        text: target?.value,
      };

      return trackFilterElementEvent(eventType, props);
    };

    /**
     * @param {function(URLSearchParams): void} callback
     */
    const updateUrlParams = (callback) => {
      const currentUrl = new URL(window.location.href);
      callback(currentUrl.searchParams);
      window.history.pushState({}, "", currentUrl.href);
    };

    /**
     * @param {any} evt
     */
    const changeHandler = (evt) => {
      resetSearch();
      const filter = makeFilter();
      updateGridUI(filter);

      updateUrlParams((params) => {
        pickers.forEach((picker, index) => {
          const param = urlParamMapping[index];
          if (!param) return;
          if (picker.selectedIndex > 0) {
            params.set(param, picker.value);
          } else {
            params.delete(param);
          }
        });
      });

      trackFilterEvent(evt);
    };

    const clearUrlParameters = () => {
      updateUrlParams((params) => {
        urlParamMapping.forEach((param) => params.delete(param));
      });
    };

    const searchChangeHandler = debounce(() => {
      if (searchInput.value) {
        resetFilter();
        clearUrlParameters();
      }
      const filter = makeFilter();
      const searchTerm = searchInput.value;
      updateGridUI(filter, searchTerm);

      trackFilterEvent(
        /** @type {any} */ ({
          type: "keyup",
          target: {
            type: "search",
            id: searchInput.id,
            value: searchInput.value,
          },
        }),
      );
    }, 400);

    pickers.forEach((picker) =>
      picker.addEventListener("change", changeHandler),
    );
    searchInput.addEventListener("input", searchChangeHandler);

    pickers.forEach((picker) => {
      picker.addEventListener("focus", trackFilterEvent);
    });
    searchInput.addEventListener("focus", trackFilterEvent);

    const urlParams = new URLSearchParams(location.search);
    urlParams.forEach((value, key) => {
      const index = urlParamMapping.indexOf(key);
      if (index !== -1 && pickers[index]) {
        pickers[index].value = value;
      }
    });

    if (pickers[0]) {
      changeHandler(/** @type {any} */ ({ target: pickers[0] }));
    }
  }
}
